import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../layout/AppLayout";
import { fetchMarketplaceList } from "../../services/accessAPI";
import { Select, Button, message, Spin } from "antd";
import DynamicFormWizard from "./DynamicFormWizard";

const { Option } = Select;

const IntermodalWizard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interfaceFrom, setInterfaceFrom] = useState(null);
  const [interfaceTo, setInterfaceTo] = useState(null);
  const [realWorldProblem, setRealWorldProblem] = useState(null);
  const [uniqueInterfaceFromOptions, setUniqueInterfaceFromOptions] = useState([]);
  const [uniqueInterfaceToOptions, setUniqueInterfaceToOptions] = useState([]);
  const [filteredRealWorldProblems, setFilteredRealWorldProblems] = useState([]);
  const [endpointDescriptions, setEndpointDescriptions] = useState([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null); // Add this state

  console.log(selectedEndpoint);
  // Fetch data on component mount
  useEffect(() => {
    const loadApiConfig = async () => {
      try {
        const apiList = await fetchMarketplaceList();
        if (apiList) {
          setData(apiList);
        } else {
          message.warning("No data available.");
        }
      } catch (error) {
        console.error("Error fetching API list:", error);
        message.error("Failed to load marketplace data.");
      } finally {
        setLoading(false);
      }
    };
    loadApiConfig();
  }, []);

  // Extract unique InterfaceFrom and InterfaceTo options
  useEffect(() => {
    const interfaceToSet = new Set();
    const interfaceFromSet = new Set();

    data.forEach((item) => {
      item.solvingProcesses?.forEach((process) => {
        process.intermodalInterfaces?.forEach((interfaceObj) => {
          if (interfaceObj.interfaceFrom) {
            interfaceFromSet.add(interfaceObj.interfaceFrom);
          }
          if (interfaceObj.interfaceTo) {
            interfaceToSet.add(interfaceObj.interfaceTo);
          }
        });
      });
    });

    setUniqueInterfaceFromOptions([...interfaceFromSet]);
    setUniqueInterfaceToOptions([...interfaceToSet]);
  }, [data]);

  // Filter Real World Problems based on InterfaceFrom and InterfaceTo
  const filterRealWorldProblems = useCallback(() => {
    const problemsSet = new Set();

    data.forEach((item) => {
      item.solvingProcesses?.forEach((process) => {
        const matches = process.intermodalInterfaces?.some(
          (interfaceObj) =>
            interfaceObj.interfaceFrom === interfaceFrom &&
            interfaceObj.interfaceTo === interfaceTo
        );
        if (matches) {
          process.realWorldProblems?.forEach((problem) => {
            problemsSet.add(problem.realWorldProblem);
          });
        }
      });
    });

    setFilteredRealWorldProblems([...problemsSet]);
  }, [data, interfaceFrom, interfaceTo]);

// Update Endpoint Descriptions when Real World Problem changes
useEffect(() => {
  if (realWorldProblem) {
    const uniqueDescriptions = new Set();
    const descriptions = [];

    data.forEach((item) => {
      item.solvingProcesses?.forEach((process) => {
        const matches = process.realWorldProblems?.some(
          (problem) => problem.realWorldProblem === realWorldProblem
        );

        if (matches) {
          item.endpoints?.forEach((endpoint) => {
            if (!uniqueDescriptions.has(endpoint.description)) {
              uniqueDescriptions.add(endpoint.description);
              // Include basePath from the parent API (item)
              descriptions.push({
                method: endpoint.method,
                path: endpoint.path,
                description: endpoint.description,
                basePath: item.basePath,
                expectedInput: endpoint.expectedInput
              });
            }
          });
        }
      });
    });

    setEndpointDescriptions(descriptions);
  } else {
    setEndpointDescriptions([]);
  }
}, [realWorldProblem, data]);
  

  return (
    <Layout>
      <div>
        <h1>Intermodal Wizard</h1>
        {loading ? (
          <Spin tip="Loading..." />
        ) : (
          <div>
            {/* InterfaceFrom Dropdown */}
            <Select
              style={{ width: "50%", marginBottom: 10 }}
              value={interfaceFrom}
              onChange={(value) => setInterfaceFrom(value)}
              placeholder="Select Interface From"
            >
              {uniqueInterfaceFromOptions.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>

            {/* InterfaceTo Dropdown */}
            <Select
              style={{ width: "50%", marginBottom: 10 }}
              value={interfaceTo}
              onChange={(value) => setInterfaceTo(value)}
              placeholder="Select Interface To"
            >
              {uniqueInterfaceToOptions.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>

            {/* Next Button */}
            <Button
              type="primary"
              onClick={filterRealWorldProblems}
              disabled={!interfaceFrom || !interfaceTo}
              style={{ marginBottom: 20 }}
            >
              Next
            </Button>

            {/* Real World Problems Dropdown */}
            {filteredRealWorldProblems.length > 0 && (
              <Select
                style={{ width: "50%", marginBottom: 10 }}
                value={realWorldProblem}
                onChange={(value) => setRealWorldProblem(value)}
                placeholder="Select Real World Problem"
              >
                {filteredRealWorldProblems.map((problem, index) => (
                  <Option key={index} value={problem}>
                    {problem}
                  </Option>
                ))}
              </Select>
            )}

            {/* Endpoint Descriptions Dropdown */}
            {endpointDescriptions.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h3>Endpoint Descriptions:</h3>
                <Select
                  style={{ width: "50%", marginBottom: 10 }}
                  placeholder="Select an Endpoint Description"
                  onChange={(value) => {
                    // Find the selected endpoint object using the description
                    const selected = endpointDescriptions.find(
                      (ep) => ep.description === value
                    );
                    setSelectedEndpoint(selected); // Store the selected endpoint
                  }}
                >
                  {endpointDescriptions.map((endpoint, index) => (
                    <Option key={index} value={endpoint.description}>
                      {endpoint.description}
                    </Option>
                  ))}
                </Select>
              </div>
            )}

            {/* Display Selected Endpoint Details */}
{selectedEndpoint && (
  <div style={{ marginTop: 20 }}>
    <h4>Selected Endpoint Details:</h4>
    <p><strong>Base Path:</strong> {selectedEndpoint.basePath}</p>
    <p><strong>Method:</strong> {selectedEndpoint.method}</p>
    <p><strong>Path:</strong> {selectedEndpoint.path}</p>
    {/* Construct the full URL */}
    <p><strong>Full URL:</strong> {selectedEndpoint.basePath}{"/"}{selectedEndpoint.path}</p>
  </div>
)}
          </div>
        )}
        <DynamicFormWizard apiData={selectedEndpoint} />;
      </div>
    </Layout>
  );
};

export default IntermodalWizard;


